import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useDimensionsList } from '../../hooks/useDimension';
import { usePipelineNodes } from '../../hooks/usePipelineNodes';
import { dimensionService } from '../../services/dimension.service';
import { SemanticLayerPageHeader, SemanticLayerToolbar } from '../../components/SemanticLayerPageHeader';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../../components/ui/pagination';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Loader2, Settings, Ruler, Plus, Search, Lock } from 'lucide-react';
import type { Dimension } from '../../services/dimension.service';
import type { PipelineNode } from '../../services/pipelineNode.service';
import { useIsInDraftMode } from '../../hooks/useDraftMode';

function getEntityAndColumnDisplay(
  dimension: Dimension,
  nodesById: Record<string, PipelineNode>
): { entity: string; column: string } {
  const node = dimension.pipeline_node_id
    ? nodesById[dimension.pipeline_node_id]
    : null;
  const entityName = node?.label || node?.name || 'Unknown';

  // Find the field within the node
  let columnName = 'Unknown';
  if (node?.fields && dimension.field_id) {
    const field = node.fields.find(
      (f) => f.id === dimension.field_id || f.name === dimension.field_id
    );
    if (field) {
      columnName = field.label || field.name || dimension.field_id;
    } else {
      columnName = dimension.field_id;
    }
  }

  return { entity: entityName, column: columnName };
}

// Convert a name to a SQL-safe column name (uppercase with underscores)
const toSqlSafeColumnName = (name: string): string => {
  return name
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
};

export default function Dimensions() {
  const isInDraftMode = useIsInDraftMode();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const pageSize = 50;

  // New dimension dialog state
  const [newDimensionDialogOpen, setNewDimensionDialogOpen] = useState(false);
  const [newDimensionName, setNewDimensionName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const { data, isLoading, error } = useDimensionsList({
    page,
    pageSize,
  });

  // Fetch pipeline nodes to display entity and column names
  const { data: nodesData, isLoading: nodesLoading } = usePipelineNodes({
    page: 1,
    pageSize: 1000, // Fetch all nodes to have them available for lookups
  });

  // Filter and sort dimensions with search
  const dimensions = useMemo(() => {
    const allDimensions = data?.records || [];

    // Apply search filter
    const filtered = searchQuery.trim()
      ? allDimensions.filter((dimension: Dimension) => {
          const name = (dimension.name || '').toLowerCase();
          const description = (dimension.description || '').toLowerCase();
          const query = searchQuery.toLowerCase();
          return name.includes(query) || description.includes(query);
        })
      : allDimensions;

    // Sort alphabetically by name
    return filtered.slice().sort((a, b) =>
      a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
    );
  }, [data?.records, searchQuery]);

  const totalPages = data?.total_pages || 1;

  // Build a lookup map for pipeline nodes
  const nodesById: Record<string, PipelineNode> = {};
  if (nodesData?.records) {
    for (const node of nodesData.records) {
      nodesById[node.id] = node;
    }
  }

  const handleConfigure = (dimensionId: string) => {
    navigate(`/develop/semantic-layer/dimensions/${dimensionId}`);
  };

  const handleCreateDimension = async () => {
    if (!newDimensionName.trim()) return;

    setIsCreating(true);
    try {
      const trimmedName = newDimensionName.trim();
      const newDimension = await dimensionService.create({
        name: trimmedName,
        column_name: toSqlSafeColumnName(trimmedName),
      });

      queryClient.invalidateQueries({ queryKey: ['dimensions'] });
      setNewDimensionDialogOpen(false);
      setNewDimensionName('');
      navigate(`/develop/semantic-layer/dimensions/${newDimension.id}`);
    } catch (error) {
      console.error('Failed to create dimension:', error);
    } finally {
      setIsCreating(false);
    }
  };

  const isLoadingAll = isLoading || nodesLoading;

  return (
    <div className="w-full flex flex-col bg-background overflow-hidden" style={{ height: 'calc(100vh)' }}>
      <SemanticLayerPageHeader tab="dimensions" />
      <SemanticLayerToolbar>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search dimensions..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-64 pl-9"
          />
        </div>
        {isInDraftMode && (
          <Button
            onClick={() => setNewDimensionDialogOpen(true)}
            className="gap-2"
          >
            <Plus className="h-4 w-4" />
            New Dimension
          </Button>
        )}
      </SemanticLayerToolbar>

      {/* Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-8 min-h-0">
        {isLoadingAll ? (
          <div className="flex items-center justify-center h-full">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">Loading dimensions...</p>
            </div>
          </div>
        ) : error ? (
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-destructive text-sm">
              Failed to load dimensions. Please try again.
            </p>
          </div>
        ) : dimensions.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-muted-foreground">No dimensions found.</p>
          </div>
        ) : (
          <>
            <div className="rounded-md border">
              <Table className="table-fixed">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[35%]">Name</TableHead>
                    <TableHead className="w-[25%]">Entity</TableHead>
                    <TableHead className="w-[25%]">Column</TableHead>
                    <TableHead className="w-[15%] text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {dimensions.map((dimension: Dimension) => {
                    const { entity, column } = getEntityAndColumnDisplay(dimension, nodesById);
                    return (
                      <TableRow key={dimension.id}>
                        <TableCell className="font-medium">
                          <div className="flex items-start gap-2">
                            <div className="flex-shrink-0 w-4 h-4 mt-0.5">
                              <Ruler className="h-4 w-4 text-muted-foreground" />
                            </div>
                            {dimension.managed_by_template_id && (
                              <Lock className="h-3 w-3 text-muted-foreground/60 shrink-0" />
                            )}
                            <div className="flex flex-col min-w-0">
                              <span>{dimension.name}</span>
                              {dimension.description && (
                                <span className="text-xs text-muted-foreground font-normal">
                                  {dimension.description}
                                </span>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {entity}
                        </TableCell>
                        <TableCell className="text-muted-foreground font-mono text-sm">
                          {column}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => handleConfigure(dimension.id)}
                            title="Configure"
                          >
                            <Settings className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="mt-6">
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          if (page > 1) setPage(page - 1);
                        }}
                        style={{ pointerEvents: page === 1 ? 'none' : 'auto', opacity: page === 1 ? 0.5 : 1 }}
                      />
                    </PaginationItem>

                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => {
                      if (
                        pageNum === 1 ||
                        pageNum === totalPages ||
                        (pageNum >= page - 1 && pageNum <= page + 1)
                      ) {
                        return (
                          <PaginationItem key={pageNum}>
                            <PaginationLink
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                setPage(pageNum);
                              }}
                              isActive={pageNum === page}
                            >
                              {pageNum}
                            </PaginationLink>
                          </PaginationItem>
                        );
                      } else if (pageNum === page - 2 || pageNum === page + 2) {
                        return (
                          <PaginationItem key={`ellipsis-${pageNum}`}>
                            <PaginationEllipsis />
                          </PaginationItem>
                        );
                      }
                      return null;
                    })}

                    <PaginationItem>
                      <PaginationNext
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          if (page < totalPages) setPage(page + 1);
                        }}
                        style={{ pointerEvents: page === totalPages ? 'none' : 'auto', opacity: page === totalPages ? 0.5 : 1 }}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            )}
          </>
        )}
      </div>

      {/* New Dimension Dialog */}
      <Dialog open={newDimensionDialogOpen} onOpenChange={setNewDimensionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Dimension</DialogTitle>
            <DialogDescription>
              Create a new dimension. You can configure its details after creation.
            </DialogDescription>
          </DialogHeader>
          <DialogBody className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="new-dimension-name">Dimension Name</Label>
              <Input
                id="new-dimension-name"
                value={newDimensionName}
                onChange={(e) => setNewDimensionName(e.target.value)}
                placeholder="Enter dimension name"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newDimensionName.trim()) {
                    handleCreateDimension();
                  }
                }}
              />
            </div>
          </DialogBody>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewDimensionDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateDimension}
              disabled={!newDimensionName.trim() || isCreating}
            >
              {isCreating ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                'Create Dimension'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
